import { useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export const TeamFormModal = ({ team, onClose, onSaved }) => {
  const { API } = useAuth();
  const [name, setName] = useState(team?.name || "");
  const [description, setDescription] = useState(team?.description || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isEdit = !!team;

  const save = async () => {
    if (!name.trim()) {
      setError("Team name is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = { name: name.trim(), description: description.trim() || null };
      const { data } = isEdit
        ? await axios.patch(`${API}/org/teams/${team.team_id}`, payload, { withCredentials: true })
        : await axios.post(`${API}/org/teams`, payload, { withCredentials: true });
      onSaved(data.team);
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save this team.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" data-testid="team-form-overlay">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-[#0B1220]" data-testid="team-form-modal">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-extrabold text-slate-950 dark:text-white">{isEdit ? "Edit Team" : "New Team"}</h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="team-form-close-btn"
            className="rounded-full border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/10"
          >
            <X size={16} />
          </button>
        </div>
        <div className="mt-4 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Team Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="team-form-name-input"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              data-testid="team-form-description-input"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white"
            />
          </div>
        </div>
        {error && (
          <p className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="team-form-error">
            {error}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="team-form-cancel-btn"
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 transition-colors duration-200 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !name.trim()}
            data-testid="team-form-save-btn"
            className="rounded-xl bg-[#0052FF] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Team"}
          </button>
        </div>
      </div>
    </div>
  );
};
